import React, { useState } from "react";
import UserSignUp from "./UserSignUp";
import ForgotPassword from "./ForgotPassword";
import {
  Box,
  Grid,
  InputAdornment,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowCircleRightOutlinedIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import { useSearchParams, useNavigate } from "react-router-dom";
import { signIn, signOut } from "aws-amplify/auth";

import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
} from "@aws-sdk/client-cognito-identity-provider";

const poolData = {
  ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
  UserPoolId: process.env.REACT_APP_USER_POOL_ID,
};

const cognitoClient = new CognitoIdentityProviderClient({
  region: process.env.REACT_APP_AWS_REGION,
});

export default function UserLogin() {
  const [user, setUser] = useState("");
  const [pwd, setPwd] = useState("");
  const [showSignUp, setShowSignUp] = useState(false); // Controls whether to show the SignUp form
  const [showReset, setShowReset] = useState(false); // Controls whether to show the Reset Password form
  const [isLoading, setIsLoading] = useState(false);
  let [searchParams] = useSearchParams();
  const navigate = useNavigate();


  const handleSubmit = async (username, password) => {
    setIsLoading(true);
    let existsInMRE = false;
    try {
      await signOut();
      setUser(username);
      const signInResponse = await signIn({ username, password });
      const authParams = {
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: poolData.ClientId,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      };

      const command = new InitiateAuthCommand(authParams);
      const result = await cognitoClient.send(command);
      const idToken = result.AuthenticationResult.IdToken;
      const accessToken = result.AuthenticationResult.AccessToken;
      const refreshToken = result.AuthenticationResult.RefreshToken;
      const expiryTime = result.AuthenticationResult.ExpiresIn;
      sessionStorage.setItem("refreshToken", refreshToken);
      sessionStorage.setItem("idToken", idToken);
      sessionStorage.setItem("expiryTime", expiryTime);

      if (
        signInResponse.nextStep.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        setIsLoading(false);
      } else {
        existsInMRE = true;
        // sessionStorage.setItem("mode", "light");
        sessionStorage.setItem("mode", "dark");
        sessionStorage.setItem("token", idToken);
        sessionStorage.setItem("userId", username);
        sessionStorage.setItem("username", username);
        sessionStorage.setItem("storyline", true);
        sessionStorage.setItem("devtools", true);
        sessionStorage.setItem("videostory", true);
      }
      return existsInMRE;
    } catch (e) {
      alert(e.message);
      return existsInMRE;
    } finally {
      // setIsLoading(false);
    }
  };

  const handleLogin = async (loginUser, loginPwd) => {
    setIsLoading(true);
    const usernameToUse = loginUser || user;
    const pwdToUse = loginPwd || pwd;
    // Perform Cognito authentication and check for existence in MRE.
    const existsInMRE = await handleSubmit(usernameToUse, pwdToUse);
    if (existsInMRE) {
      try {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate("/");
        window.location.reload();
      } catch (err) {
        sessionStorage.clear();
        return navigate("/user/login");
      }
    } else {
      // If the user does NOT exist in the HC MRE account, use the existing flow.
      const idToken = sessionStorage.getItem("idToken");
      const accessToken = sessionStorage.getItem("accessToken");
      // sessionStorage.setItem("mode", "light");
      sessionStorage.setItem("mode", "dark");
      sessionStorage.setItem("tokens", idToken);
      sessionStorage.setItem("userId", usernameToUse);
      sessionStorage.setItem("username", usernameToUse);
      sessionStorage.setItem("storyline", "");
      sessionStorage.setItem("devtools", "");
      sessionStorage.setItem("videostory", "");
      setIsLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate("/");
      window.location.reload();
    }
  };

  if (showSignUp) {
    return (
      <UserSignUp
        onSignUpComplete={async (username, password) => {
          setShowSignUp(false);
          if (username && password) {
            setUser(username);
            setPwd(password);
            await handleLogin(username, password);
          }
        }}
        setShowSignUp={setShowSignUp}
      />
    );
  }

  if (showReset) {
    return (
      <ForgotPassword setShowReset={setShowReset} />
    );
  }

  return (
    <Grid container spacing={0}>
      <Grid item sm={12} md={3} lg={4} />
      <Grid item sm={12} md={6} lg={4}>
        <div className="loginCard">
          <h1
            className="gradientText"
            style={{
              fontWeight: 900,
              fontSize: "36px",
              lineHeight: 1.2,
              textTransform: "capitalize",
              margin: 0,
            }}
          >
            Automated Video Storytelling from Real-World Videos
          </h1>
          <Box
            component="form"
            sx={{ "& .MuiTextField-root": { my: 1 }, marginTop: "24px" }}
            noValidate
            autoComplete="off"
          >
            <TextField
              fullWidth
              autoFocus
              required
              label="Username"
              variant="standard"
              onChange={(e) => setUser(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleLogin()}
            />
            <TextField
              fullWidth
              required
              label="Password"
              type="password"
              variant="standard"
              onChange={(e) => setPwd(e.target.value)}
              onKeyUp={(e) => e.key === "Enter" && handleLogin()}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="start"
                    style={{ marginTop: -14, marginRight: 0 }}
                  >
                    {isLoading ? (
                      <CircularProgress
                        size={24}
                        thickness={8}
                        sx={{
                          color: "#AC1FFF",
                          marginRight: "10px",
                        }}
                      />
                    ) : (
                      <IconButton onClick={(e) => handleLogin()}>
                        <ArrowCircleRightOutlinedIcon fontSize="large" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            {searchParams.get("loginerror") && (
              <h4 style={{ color: "red", textAlign: "center" }}>
                Invalid username or password
              </h4>
            )}
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="text"
                color="secondary"
                onClick={() => setShowReset(true)}
              >
                Forgot Password?
              </Button>
            </Box>
            {/* Button to switch to the SignUp form */}
            <Button
              fullWidth
              variant="text"
              color="secondary"
              onClick={() => setShowSignUp(true)}
            >
              Don't have an account? Sign Up
            </Button>
          </Box>
        </div>
      </Grid>
      <Grid item sm={12} md={3} lg={4} />
    </Grid>
  );
}
